
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Printer, Loader2, FileText } from 'lucide-react';
import { Order } from '@/types';
import { useToast } from '@/hooks/use-toast';
import PedidoPreviewDialog from '@/components/orders/PedidoPreviewDialog';
import { getPedidoCompleto } from '@/services/orderApiService';
import type { PedidoCompleto } from '@/services/orderApiService';

interface OrderReprintButtonProps {
  order: Order;
  size?: 'default' | 'sm' | 'lg' | 'icon';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  className?: string;
  faturar?: boolean;
  onViewDanfe?: (orderId: number) => void;
}

const OrderReprintButton: React.FC<OrderReprintButtonProps> = ({
  order,
  size = 'sm',
  variant = 'outline',
  className = '',
  faturar = false,
  onViewDanfe
}) => {
  const [isPrinting, setIsPrinting] = React.useState(false);
  const [pedidoPreviewOpen, setPedidoPreviewOpen] = React.useState(false);
  const [pedidoPreviewLoading, setPedidoPreviewLoading] = React.useState(false);
  const [pedidoPreviewError, setPedidoPreviewError] = React.useState<string | null>(null);
  const [pedidoPreview, setPedidoPreview] = React.useState<PedidoCompleto | null>(null);
  const { toast } = useToast();

  const tableNumber = React.useMemo(() => {
    if (order.type !== 'dine-in') return undefined;
    if (order.tableNumber !== undefined && order.tableNumber !== null) {
      const text = String(order.tableNumber).trim();
      return text ? text : undefined;
    }
    if (!order.tableId) return undefined;
    const tablePart = String(order.tableId).split('-')[0];
    const trimmed = tablePart.trim();
    return trimmed ? trimmed : undefined;
  }, [order.tableId, order.type]);
  
  const handleReprint = async () => {
    if (!order.erpId || Number(order.erpId) <= 0) {
      toast({
        title: 'Reimpressão indisponível',
        description: 'Este pedido não possui ID da venda no ERP para obter o comprovante.',
        variant: 'destructive',
      });
      return;
    }

    setIsPrinting(true);
    setPedidoPreviewOpen(true);
    setPedidoPreviewLoading(true);
    setPedidoPreviewError(null);
    setPedidoPreview(null);

    try {
      const pedido = await getPedidoCompleto(Number(order.erpId));
      setPedidoPreview(pedido);

      toast({
        title: 'Comprovante carregado',
        description: `Pedido #${order.id.substring(0, 8)} pronto para impressão${faturar ? ' (com faturamento automático)' : ''}.`,
      });
    } catch (error) {
      console.error('Erro ao obter pedido completo:', error);
      setPedidoPreviewError(error instanceof Error ? error.message : 'Não foi possível carregar o pedido.');
      toast({
        title: 'Erro ao reimprimir',
        description: error instanceof Error ? error.message : 'Não foi possível obter o comprovante do pedido.',
        variant: 'destructive',
      });
    } finally {
      setPedidoPreviewLoading(false);
      setIsPrinting(false);
    }
  };
  
  const handleViewDanfe = () => {
    if (order.erpId && onViewDanfe) {
      onViewDanfe(order.erpId);
    } else {
      toast({
        title: "DANFE não disponível",
        description: "Este pedido não possui um ID ERP associado para visualizar o DANFE",
        variant: "destructive"
      });
    }
  };
  
  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          variant={variant}
          size={size}
          className={`flex items-center gap-1 ${className}`}
          onClick={handleReprint}
          disabled={isPrinting}
        >
          {isPrinting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Printer className="h-3 w-3" />
          )}
          <span>Reimprimir</span>
        </Button>

        {order.erpId && onViewDanfe && (
          <Button
            variant="secondary"
            size={size}
            className="flex items-center gap-1"
            onClick={handleViewDanfe}
          >
            <FileText className="h-3 w-3" />
            <span>DANFE</span>
          </Button>
        )}
      </div>

      <PedidoPreviewDialog
        open={pedidoPreviewOpen}
        onOpenChange={(open) => {
          setPedidoPreviewOpen(open);
          if (!open) {
            setPedidoPreview(null);
            setPedidoPreviewError(null);
          }
        }}
        pedido={pedidoPreview}
        isLoading={pedidoPreviewLoading}
        error={pedidoPreviewError}
        tableNumber={tableNumber}
      />
    </>
  );
};

export default OrderReprintButton;
